import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { UserCircle, Search, Menu, X, ChevronDown, LogOut, LayoutDashboard } from "lucide-react";
import { logoutMerchant } from "../../features/merchant/merchantThunks";
import logo from "../../assets/images/mszila-border-logo.png";
import { toast } from "react-toastify";
import { navigateTo } from "../../utils/navigation";

const Header = () => {
  const dispatch = useDispatch();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const [isRegisterOpen, setIsRegisterOpen] = useState(false);
  const { user, accessToken } = useSelector((state) => state.merchant);
  const isLoggedIn = Boolean(user && accessToken);

  // Persist authentication state
  useEffect(() => {
    if (user && accessToken) {
      sessionStorage.setItem('merchantUser', JSON.stringify(user));
      sessionStorage.setItem('merchantToken', accessToken);
    }
  }, [user, accessToken]);

  // Restore authentication state on mount
  useEffect(() => {
    const savedUser = sessionStorage.getItem('merchantUser');
    const savedToken = sessionStorage.getItem('merchantToken');

    if (savedUser && savedToken && !user) {

      // dispatch(setMerchant({ user: JSON.parse(savedUser), accessToken: savedToken }));
    }
  }, []);

const handleLogout = async () => {
  await dispatch(logoutMerchant());

  toast.success("Logged out successfully!");

  navigateTo("/merchant-login");
};

  const displayName = user?.fullName || "User";

  const profileImageUrl = user?.profileImage
    ? `${process.env.REACT_APP_IMAGE_BASE_URL}${user.profileImage}`
    : null;

  const dashboardLink = "/dashboard";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close mobile menu on resize
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth >= 992) {
        setIsMobileMenuOpen(false);
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // USER DROPDOWN
  const UserMenu = () => (
    <div className="user-menu-wrapper">
      <button
        className="user-menu-button"
        onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
        aria-expanded={isUserMenuOpen}
      >
        <div className="user-avatar">
          {profileImageUrl ? (
            <img
              src={profileImageUrl}
              alt="Profile"
              className="avatar-image"
            />
          ) : (
            <div className="avatar-placeholder">
              <UserCircle size={24} />
            </div>
          )}
        </div>
        <span className="user-name">{displayName}</span>
        <ChevronDown size={16} className={`chevron-icon ${isUserMenuOpen ? 'rotated' : ''}`} />
      </button>

      {isUserMenuOpen && (
        <>
          <div className="dropdown-overlay" onClick={() => setIsUserMenuOpen(false)} />
          <div className="user-dropdown">
            <div className="dropdown-header">
              <div className="dropdown-user-info">
                <div className="dropdown-avatar">
                  {profileImageUrl ? (
                    <img src={profileImageUrl} alt="Profile" />
                  ) : (
                    <UserCircle size={32} />
                  )}
                </div>
                <div className="dropdown-user-details">
                  <p className="dropdown-user-name">{displayName}</p>
                  <p className="dropdown-user-email">{user?.email || ''}</p>
                </div>
              </div>
            </div>
            <div className="dropdown-divider" />
           <button
  className="dropdown-item"
  onClick={() => navigateTo("/dashboard")}
>
  <LayoutDashboard size={18} />
  <span>Dashboard</span>
</button>

            <div className="dropdown-divider" />
            <button className="dropdown-item dropdown-item-danger" onClick={handleLogout}>
              <LogOut size={18} />
              <span>Logout</span>
            </button>
          </div>
        </>
      )}
    </div>
  );

  // LOGIN / REGISTER BUTTONS
const AuthButtons = () => (
  <div className="auth-buttons register-wrapper">
    <a href="/merchant-login" className="btn-auth btn-login">
      Log in
    </a>

    {/* REGISTER DROPDOWN */}
    <div className="register-dropdown-wrapper">
      <button
        className="btn-auth btn-register"
        onClick={() => setIsRegisterOpen(!isRegisterOpen)}
        type="button"
      >
        Register <ChevronDown size={16} />
      </button>

      {isRegisterOpen && (
        <>
          <div
            className="dropdown-overlay"
            onClick={() => setIsRegisterOpen(false)}
          />

          <div className="register-dropdown">
            <a
              href="/register"
              className="register-dropdown-item"
              onClick={() => setIsRegisterOpen(false)}
            >
              Register as User
            </a>

            <a
              href="/merchant-register"
              className="register-dropdown-item"
              onClick={() => setIsRegisterOpen(false)}
            >
              Register as Merchant
            </a>
          </div>
        </>
      )}
    </div>
  </div>
);


  return (
    <>
      <style>{`
        :root {
          --color-primary: #6052a5;
          --color-primary-dark: #4a3f85;
          --color-primary-light: #4B3D9A;
          --color-secondary: #e5e7eb;
          --color-text-dark: #1f2937;
          --color-text-light: #6b7280;
          --color-background-light: #f9fafb;
          --color-danger: #ef4444;
          --color-danger-light: #fee2e2;
          --color-placeholder: #95a5a6;
          --color-border: #bdc3c7;
        }

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        .enhanced-header {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          z-index: 1000;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          background: #4F419C;
        }

       

        .header-container {
          max-width: 1400px;
          margin: 0 auto;
          padding: 0.5rem 2rem;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 2rem;
        }

        .logo-section {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          text-decoration: none;
          transition: transform 0.3s ease;
          flex-shrink: 0;
        }

        .logo-section:hover {
          transform: scale(1.05);
        }

        .logo-circle {
          width: 45px;
          height: 45px;
          background: linear-gradient(135deg, var(--color-primary), var(--color-primary-light));
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          box-shadow: 0 4px 12px rgba(96, 82, 165, 0.3);
          transition: all 0.3s ease;
        }

        .logo-section:hover .logo-circle {
          box-shadow: 0 6px 20px rgba(96, 82, 165, 0.4);
          transform: rotate(5deg);
        }

        .logo-text {
          color: white;
          font-size: 1.5rem;
          font-weight: 700;
          font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        .brand-text {
          font-size: 1.5rem;
          font-weight: 700;
          color: white;
          letter-spacing: 0.5px;
          transition: color 0.3s ease;
        }

     

        .nav-section {
          display: flex;
          align-items: center;
          gap: 0.5rem;
          list-style: none;
        }

        .nav-link-item {
          position: relative;
          padding: 0.5rem 1rem;
          color: white;
          text-decoration: none;
          font-weight: 500;
          font-size: 0.95rem;
          transition: all 0.3s ease;
          border-radius: 8px;
        }

       

        .nav-link-item:hover {
          color: white;
          background: rgba(255, 255, 255, 0.15);
        }

      

        .nav-link-item.active {
          color: white;
          background: rgba(255, 255, 255, 0.2);
        }

       

        .nav-link-item::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 50%;
          transform: translateX(-50%);
          width: 0;
          height: 2px;
          background: white;
          transition: width 0.3s ease;
        }

       

        .nav-link-item:hover::after,
        .nav-link-item.active::after {
          width: 60%;
        }

        .actions-section {
          display: flex;
          align-items: center;
          gap: 1rem;
          flex-shrink: 0;
        }

        .search-button {
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.15);
          border: none;
          border-radius: 10px;
          color: white;
          cursor: pointer;
          transition: all 0.3s ease;
          text-decoration: none;
        }

        

        .search-button:hover {
          background: white;
          color: var(--color-primary);
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(96, 82, 165, 0.3);
        }

        
        .auth-buttons {
          display: flex;
          align-items: center;
          gap: 0.75rem;
        }

        .btn-auth {
          padding: 0.6rem 1.5rem;
          border-radius: 10px;
          font-weight: 600;
          font-size: 0.9rem;
          text-decoration: none;
          transition: all 0.3s ease;
          border: 2px solid transparent;
          white-space: nowrap;
        }

        .btn-login {
          color: #fdfcfcee;
          background: transparent;
          border-color: #fdfcfcd4;
        }

        .btn-login:hover {
          background: var(--color-primary);
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(96, 82, 165, 0.3);
        }

        .btn-register {
          background: var(--color-primary);
          color: white;
        }

        .btn-register:hover {
          background: var(--color-primary-dark);
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(96, 82, 165, 0.4);
        }

        .user-menu-wrapper {
          position: relative;
        }

        .user-menu-button {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.3rem 0.5rem;
          background: var(--color-background-light);
          border: 2px solid transparent;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          font-weight: 500;
          color: var(--color-text-dark);
        }

        .user-menu-button:hover {
          background: white;
          border-color: var(--color-primary);
          box-shadow: 0 4px 12px rgba(96, 82, 165, 0.2);
        }

        .user-avatar {
          width: 30px;
          height: 30px;
          border-radius: 50%;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          background: var(--color-primary-light);
          color: white;
          flex-shrink: 0;
        }

        .avatar-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .avatar-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .user-name {
          font-size: 0.95rem;
          max-width: 150px;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .chevron-icon {
          transition: transform 0.3s ease;
          flex-shrink: 0;
        }

        .chevron-icon.rotated {
          transform: rotate(180deg);
        }

        .dropdown-overlay {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 999;
        }

        .user-dropdown {
          position: absolute;
          top: calc(100% + 0.5rem);
          right: 0;
          min-width: 280px;
          background: white;
          border-radius: 12px;
          box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
          z-index: 1000;
          overflow: hidden;
          animation: dropdownSlide 0.3s ease;
        }

        @keyframes dropdownSlide {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        .dropdown-header {
          padding: 1.25rem;
          background: linear-gradient(135deg, var(--color-primary), var(--color-primary-light));
        }

        .dropdown-user-info {
          display: flex;
          align-items: center;
          gap: 1rem;
        }

        .dropdown-avatar {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          overflow: hidden;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(255, 255, 255, 0.2);
          color: white;
          flex-shrink: 0;
        }

        .dropdown-avatar img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .dropdown-user-details {
          flex: 1;
          min-width: 0;
        }

        .dropdown-user-name {
          color: white;
          font-weight: 600;
          font-size: 1rem;
          margin: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .dropdown-user-email {
          color: rgba(255, 255, 255, 0.9);
          font-size: 0.85rem;
          margin: 0.25rem 0 0 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        .dropdown-divider {
          height: 1px;
          background: var(--color-secondary);
          margin: 0;
        }

        .dropdown-item {
          display: flex;
          align-items: center;
          gap: 0.75rem;
          padding: 0.875rem 1.25rem;
          color: var(--color-text-dark);
          text-decoration: none;
          background: none;
          border: none;
          width: 100%;
          text-align: left;
          font-size: 0.95rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .dropdown-item:hover {
          background: var(--color-background-light);
          color: var(--color-primary);
        }

        .dropdown-item-danger {
          color: var(--color-danger);
        }

        .dropdown-item-danger:hover {
          background: var(--color-danger-light);
          color: var(--color-danger);
        }

        .mobile-toggle {
          display: none;
          width: 40px;
          height: 40px;
          align-items: center;
          justify-content: center;
          background: var(--color-background-light);
          border: none;
          border-radius: 10px;
          color: var(--color-text-dark);
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .mobile-toggle:hover {
          background: var(--color-primary);
          color: white;
        }

        @media (max-width: 991px) {
          .header-container {
            padding: 1rem 1.5rem;
          }

          .nav-section {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: #4B3D9A;
            flex-direction: column;
            justify-content: flex-start;
            align-items: stretch;
            padding: 5rem 2rem 2rem;
            gap: 0;
            transform: translateX(-100%);
            transition: transform 0.3s ease;
            overflow-y: auto;
          }

          .nav-section.open {
            transform: translateX(0);
          }

          .nav-link-item {
            padding: 1rem 1.5rem;
            border-radius: 0;
            border-bottom: 1px solid var(--color-secondary);
          }

          .nav-link-item::after {
            display: none;
          }

          .mobile-toggle {
            display: flex;
          }

          .user-name {
            display: none;
          }

          .btn-auth {
            padding: 0.5rem 1rem;
            font-size: 0.85rem;
          }
        }

        @media (max-width: 576px) {
          .header-container {
            padding: 0.875rem 1rem;
            gap: 1rem;
          }

          .logo-circle {
            width: 38px;
            height: 38px;
          }

          .logo-text {
            font-size: 1.25rem;
          }

          .brand-text {
            font-size: 1.25rem;
          }

          .search-button {
            width: 36px;
            height: 36px;
          }

          .actions-section {
            gap: 0.5rem;
          }

          .auth-buttons {
            gap: 0.5rem;
          }

          .btn-auth {
            padding: 0.5rem 0.875rem;
            font-size: 0.8rem;
          }

          .user-dropdown {
            min-width: 260px;
            right: -1rem;
          }
        }

        @media (max-width: 400px) {
          .brand-text {
            display: none;
          }

          .btn-login {
            display: none;
          }
        }
          .navbar-logo {
  height: 55px;
  width: auto;
  object-fit: contain;
  transition: transform 0.3s ease;
}

.logo-section:hover .navbar-logo {
  transform: scale(1.05);
}

/* Mobile */
@media (max-width: 576px) {
  .navbar-logo {
    height: 50px;
  }
}

/* REGISTER DROPDOWN */
.register-wrapper {
  position: relative;
}

.register-dropdown-wrapper {
  position: relative;
}

.register-dropdown {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 220px;
  background: white;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  z-index: 1000;
  animation: dropdownSlide 0.25s ease;
}

.register-dropdown-item {
  display: block;
  padding: 12px 16px;
  text-decoration: none;
  color: var(--color-text-dark);
  font-weight: 500;
  transition: background 0.2s ease, color 0.2s ease;
}

.register-dropdown-item:hover {
  background: var(--color-background-light);
  color: var(--color-primary);
}

// side bar open for mobile 
/* HIDE HEADER TOGGLE WHEN MENU OPEN */
.mobile-toggle.hidden {
  display: none;
}

/* SIDE NAV HEADER */
.mobile-nav-header {
  display: none;
}

/* MOBILE SIDE NAV HEADER */
@media (max-width: 991px) {
  .mobile-nav-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 1rem 1.5rem;
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    background: #4B3D9A;
    z-index: 1001;
  }

  .mobile-nav-logo {
    height: 45px;
    object-fit: contain;
  }

  .mobile-nav-close {
    background: transparent;
    border: none;
    color: white;
    cursor: pointer;
    padding: 0.25rem;
  }

  .nav-section {
    padding-top: 5.5rem; /* space for logo + close */
  }
}
      `}</style>

      <header className={`enhanced-header ${isScrolled ? 'scrolled' : ''}`}>
        <div className="header-container">
          <a className="logo-section" href="/">
            <img
          src={logo}
         alt="MSZILA Logo"
          className="navbar-logo"
         />
       </a>


         <nav className={`nav-section ${isMobileMenuOpen ? "open" : ""}`}>
  {/* SIDE NAV HEADER (MOBILE ONLY) */}
  <div className="mobile-nav-header">
    <img src={logo} alt="MSZILA Logo" className="mobile-nav-logo" />
    <button
      className="mobile-nav-close"
      onClick={() => setIsMobileMenuOpen(false)}
      aria-label="Close menu"
    >
      <X size={26} />
    </button>
  </div>

  <a className="nav-link-item active" href="/" onClick={() => setIsMobileMenuOpen(false)}>
    Home
  </a>
  <a className="nav-link-item" href="/merchant-search" onClick={() => setIsMobileMenuOpen(false)}>
    Categories
  </a>
  <a className="nav-link-item" href="/about" onClick={() => setIsMobileMenuOpen(false)}>
    About Us
  </a>
  <a className="nav-link-item" href="/contact" onClick={() => setIsMobileMenuOpen(false)}>
    Contact Us
  </a>
</nav>


          <div className="actions-section">
            <a className="search-button" href="/merchant-search">
              <Search size={20} />
            </a>

            {isLoggedIn ? <UserMenu /> : <AuthButtons />}

            <button
              className="mobile-toggle"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label="Toggle menu">
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </div>
      </header>
    </>
  );
};

export default Header;